import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getOrganization, updateOrganization, withOrganizationUpdateRequired } from "../../api/api";
import { defaultPfpUrl } from "../utils";
import OrganizationProfileInfo from "./OrganizationProfileInfo";
import OrganizationProfileHeader from "./OrganizationProfileHeader";
import { OrganizationProfileSidebar } from "./ProfileSidebar";
import { OrganizationProfileEditField } from "./ProfileEditField";
import { FormattedBody } from "../ui/formatted-body";

/**
 * Organization profile: header, about/staff/posts, or edit mode.
 * @param {Object} props
 * @param {number} props.userId
 * @param {Object} props.user
 * @param {function} [props.refreshUser]
 * @returns {JSX.Element}
 */
const OrganizationProfilePage = ({ userId, user, refreshUser }) => {
    const { orgId } = useParams();
    const [organization, setOrganization] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    const targetOrgId = orgId || userId;

    const handleSave = async (updatedData) => {
        try {
            updatedData.links = updatedData.links.map(link => ({
                label: link.label,
                url: link.url
            }));
            const result = await updateOrganization(
                orgId,
                withOrganizationUpdateRequired(organization, updatedData)
            );
            if (result.error) {
                console.error("Error updating organization:", result.error);
            } else {
                setOrganization(result.data);
                setIsEditing(false);
                // If editing own organization, refresh the user in App to update header
                if (orgId === userId && refreshUser) {
                    await refreshUser();
                }
            }
        } catch (error) {
            console.error("Error updating organization:", error);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
    };

    useEffect(() => {
        const fetchOrganization = async () => {
            if (!orgId) {
                setError("No organization ID provided");
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                const result = await getOrganization(userId, orgId);
                if (result.error) {
                    setError(result.error);
                } else {
                    setOrganization(result.data);
                }
            } catch (err) {
                setError("Failed to load organization profile");
            } finally {
                setLoading(false);
            }
        };
        fetchOrganization();
    }, [orgId]);

    if (loading) {
        return (
            <div className="flex min-h-[calc(100dvh-3.5rem)] items-center justify-center bg-primary">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
                    <p className="mt-4 text-text-secondary">Loading organization profile...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex min-h-[calc(100dvh-3.5rem)] items-center justify-center bg-primary">
                <div className="text-center">
                    <div className="text-error text-6xl mb-4">⚠️</div>
                    <h2 className="text-2xl font-bold text-text-primary mb-2">Error Loading Organization</h2>
                    <p className="text-secondary">{error}</p>
                </div>
            </div>
        );
    }

    if (!organization) {
        return (
            <div className="flex min-h-[calc(100dvh-3.5rem)] items-center justify-center bg-primary">
                <div className="text-center">
                    <div className="text-muted text-6xl mb-4">🏢</div>
                    <h2 className="text-2xl font-bold text-text-primary mb-2">Organization Not Found</h2>
                    <p className="text-secondary">The requested organization profile could not be found.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[calc(100dvh-3.5rem)] max-w-full overflow-x-clip bg-primary pb-8">
            {/* Header Section */}
            <OrganizationProfileHeader 
                organization={organization}
                userId={userId}
                targetUserId={targetOrgId}
                isEditing={isEditing}
                onEdit={() => setIsEditing(true)}
            />

            {/* Mission Statement */}
            {organization.mission_statement && !isEditing && (
                <div className="w-full bg-primary py-8 px-4 mt-2 mb-0">
                    <FormattedBody
                        as="h2"
                        className="text-center text-lg font-medium italic text-text-secondary"
                    >
                        {organization.mission_statement}
                    </FormattedBody>
                </div>
            )}

            {/* Main Content */}
            <div className="max-w-4xl mx-auto px-4 py-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Organization Information */}
                    <div className="lg:col-span-2">
                        {isEditing ? (
                            <OrganizationProfileEditField
                                organization={organization}
                                onSave={handleSave}
                                onCancel={handleCancel}
                            />
                        ) : (
                            <OrganizationProfileInfo
                                organization={organization}
                                userId={userId}
                                viewerUser={user}
                            />
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="lg:col-span-1">
                        <OrganizationProfileSidebar
                            organization={organization}
                            isEditing={isEditing}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrganizationProfilePage;
